import { ApiError, normalizeApiText, type ReportVerificationInfo } from "./api";

// Scan statuses after which the report can be downloaded (planning#213,
// "Когда доступен"). `target_unreachable` is in the backend's
// TERMINAL_SCAN_STATUSES even though the older OpenAPI text lists only the
// first three. Until one of these, GET /report answers 409 report_not_ready.
const TERMINAL_SCAN_STATUSES = new Set(["completed", "failed", "cancelled", "target_unreachable"]);

export function isTerminalScanStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  return TERMINAL_SCAN_STATUSES.has(normalizeApiText(status));
}

export type ReportVerificationBadge = {
  // pending — no pentester has finalized this report yet (automated composition).
  // verified — the current act is valid and matches the current composition.
  // stale — the composition or decisions changed after the act; the saved
  //         reviewer/time are history, not a live attestation.
  // revoked — the last act was explicitly withdrawn by an admin.
  kind: "pending" | "verified" | "stale" | "revoked";
  reviewer: string | null;
  verifiedAt: string | null;
};

/**
 * What the Reports page should say about manual attestation. Returns null only
 * for an unknown future state.
 *
 * The spec is explicit that "проверен пентестером" may rest on `is_verified`
 * only — never on `status`/`manual_verification_state` alone — so an
 * inconsistent payload that says `verified` with `is_verified=false` is
 * downgraded to stale rather than shown green.
 */
export function reportVerificationBadge(
  info: ReportVerificationInfo,
): ReportVerificationBadge | null {
  const reviewer = reviewerName(info);
  const verifiedAt = info.verified_at ?? null;
  if (info.is_verified) return { kind: "verified", reviewer, verifiedAt };
  switch (info.manual_verification_state) {
    case "not_completed":
      return { kind: "pending", reviewer, verifiedAt };
    case "verified":
    case "stale":
      return { kind: "stale", reviewer, verifiedAt };
    case "revoked":
      return { kind: "revoked", reviewer, verifiedAt };
    default:
      return null;
  }
}

function reviewerName(info: ReportVerificationInfo): string | null {
  const by = info.verified_by;
  if (!by) return null;
  const name = by.display_name.trim() || by.user_id.trim();
  return name || null;
}

/** i18n key for a failed report download / preview. */
export function reportDownloadErrorKey(err: unknown): "reports.notReady" | "reports.downloadFailed" {
  if (err instanceof ApiError && err.status === 409) return "reports.notReady";
  return "reports.downloadFailed";
}

/**
 * Owner-side consequence of strict mode (planning#213, "Правило включения
 * находок"): after the first finalization `/findings` returns only the current
 * manual Confirms, so pending and false-positive findings silently vanish from
 * the list. `current_counts` still describes the whole canonical review set,
 * which lets us say how many were left out.
 */
export function strictModeNotice(
  info: ReportVerificationInfo | null | undefined,
): { confirmed: number; excluded: number } | null {
  if (!info?.strict_mode) return null;
  const c = info.current_counts;
  return { confirmed: c.confirmed, excluded: c.unreviewed + c.false_positive };
}

/**
 * The gateway always asks for the first page of 200 findings and exposes no
 * HTTP pagination; `total` comes from the upstream total_count and may exceed
 * the array length. Null when everything fits or the total is unknown.
 */
export function truncatedNotice(
  shown: number,
  total: number | null | undefined,
): { shown: number; total: number } | null {
  if (!total || total <= shown) return null;
  return { shown, total };
}

export type ReportVerificationLine = {
  key:
    | "reports.tipPending"
    | "reports.tipVerified"
    | "reports.tipStale"
    | "reports.tipRevoked"
    | "reports.tipCurrentCounts"
    | "reports.tipSnapshotCounts";
  vars: Record<string, string | number>;
};

const DASH = "—";

/**
 * Lines for the attestation badge tooltip: state + reviewer/history + counts
 * (planning#213 call sequence: "показать current state, reviewer/history/counts").
 * Verified shows the finalized snapshot; every other state shows the live
 * review counts, since the snapshot is history there.
 */
export function reportVerificationTooltipLines(
  info: ReportVerificationInfo,
  formatDate: (iso: string) => string,
): ReportVerificationLine[] {
  const badge = reportVerificationBadge(info);
  if (!badge) return [];
  const who = { name: badge.reviewer ?? DASH, date: badge.verifiedAt ? formatDate(badge.verifiedAt) : DASH };
  const c = info.current_counts;
  const current: ReportVerificationLine = {
    key: "reports.tipCurrentCounts",
    vars: { total: c.total, confirmed: c.confirmed, unreviewed: c.unreviewed, falsePositive: c.false_positive },
  };
  switch (badge.kind) {
    case "pending":
      return [{ key: "reports.tipPending", vars: {} }, current];
    case "verified": {
      const s = info.snapshot_counts;
      const snapshot: ReportVerificationLine = s
        ? {
            key: "reports.tipSnapshotCounts",
            vars: {
              included: s.included,
              total: s.total,
              excludedUnreviewed: s.excluded_unreviewed,
              excludedFalsePositive: s.excluded_false_positive,
            },
          }
        : current;
      return [{ key: "reports.tipVerified", vars: who }, snapshot];
    }
    case "stale":
      return [{ key: "reports.tipStale", vars: who }, current];
    case "revoked":
      return [{ key: "reports.tipRevoked", vars: who }, current];
  }
}

